/**
 * The resonance meter's model (M5 spec §10, §15.4; plan C18): the gate's stability as a vertical gauge 0-100 with
 * marks at the stage boundaries (60 flickers, 30 tremors), the value, and a 10-day trend.
 *
 * - **Tier gate:** the meter shows only from `storyTier ≥ 3` (the worker also sends `GateView.stability` only from
 *   tier 3). A dev override (true or false) wins over the tier.
 * - **Trend:** one sample per Reach day in a fixed ring (`trendDays`); the newest sample follows today's value, a new
 *   day starts a new sample. Allocation-free after creation.
 */

export interface ResonanceState {
  /** Ring length (days). */
  readonly days: number
  /** Samples, a ring: slot `(head + i) % days` is the i-th oldest. */
  readonly samples: Float32Array
  count: number
  head: number
  /** The day of the newest sample (−1 = none yet). */
  lastDay: number
  /** The latest stability (NaN = unknown: before tier 3, or no gate view yet). */
  value: number
  /** Bumps whenever a sample changes (the view redraws the trend bars). */
  version: number
}

export function createResonance(days: number): ResonanceState {
  const n = Math.max(1, Math.floor(days))
  return { days: n, samples: new Float32Array(n), count: 0, head: 0, lastDay: -1, value: Number.NaN, version: 0 }
}

export function resetResonance(r: ResonanceState): void {
  r.samples.fill(0)
  r.count = 0
  r.head = 0
  r.lastDay = -1
  r.value = Number.NaN
  r.version++
}

function clamp100(v: number): number {
  return v < 0 ? 0 : v > 100 ? 100 : v
}

/**
 * Records `stability` for Reach day `day` (null = unknown: the value goes NaN, the trend is kept). The same day
 * overwrites the newest sample; a later day appends (the oldest drops when the ring is full). Returns true when the
 * value or the trend changed.
 */
export function sampleResonance(r: ResonanceState, day: number, stability: number | null): boolean {
  if (stability === null || !Number.isFinite(stability)) {
    if (Number.isNaN(r.value)) return false
    r.value = Number.NaN
    r.version++
    return true
  }
  const v = clamp100(stability)
  const d = Math.floor(day)
  if (r.count > 0 && d <= r.lastDay) {
    const newest = (r.head + r.count - 1) % r.days
    if (r.samples[newest] === Math.fround(v) && r.value === v) return false
    r.samples[newest] = v
  } else {
    if (r.count < r.days) {
      r.samples[(r.head + r.count) % r.days] = v
      r.count++
    } else {
      r.samples[r.head] = v
      r.head = (r.head + 1) % r.days
    }
    r.lastDay = d
  }
  r.value = v
  r.version++
  return true
}

/** The i-th oldest sample (0 ≤ i < count); NaN outside. */
export function trendSample(r: ResonanceState, i: number): number {
  if (i < 0 || i >= r.count) return Number.NaN
  return r.samples[(r.head + i) % r.days]
}

/** Newest minus oldest over the ring (0 with fewer than two samples). */
export function trendDelta(r: ResonanceState): number {
  if (r.count < 2) return 0
  return trendSample(r, r.count - 1) - trendSample(r, 0)
}

/** The trend line under the value: "▲ +4 / 10 d", "▼ −12 / 10 d", "— steady". */
export function trendText(r: ResonanceState): string {
  const d = Math.round(trendDelta(r))
  if (r.count < 2 || d === 0) return '— steady'
  return d > 0 ? `▲ +${d} / ${r.count} d` : `▼ −${-d} / ${r.count} d`
}

/**
 * Whether the meter shows: the dev override when set, else `storyTier ≥ 3` with the resonance meter module fitted
 * (M7 S37: the Mission 2 reward after help or stand off; `fitted` defaults to true for callers without a career).
 */
export function meterVisible(tier: number, override: boolean | null, fitted = true): boolean {
  return override ?? (tier >= 3 && fitted)
}

/** The stage band of a value by the marks (descending, e.g. [60, 30]): 0 above the first, 1 below it, 2 below the second. */
export function meterBand(value: number, marks: readonly number[]): number {
  if (Number.isNaN(value)) return 0
  let band = 0
  for (let i = 0; i < marks.length; i++) if (value < marks[i]) band = i + 1
  return band
}
